<template>
  <!--
    Zwei-Faktor-Anmeldung (backend#9), zweiter Schritt nach dem Passwort.

    "code":       Code aus der Authenticator-App oder ein Wiederherstellungscode.
    "einrichten": Die Organisation verlangt 2FA - QR-Code scannen, ersten Code
                  bestaetigen, Wiederherstellungscodes sichern, dann weiter.

    Weiter zur Plattform geht es ueber das Ereignis "angemeldet"; die
    Weiterleitung mit Einmal-Code (login#2) liegt in LoginForm.
  -->
  <v-card-text data-cy="zweiFaktor">
    <!-- ======================================================= Code -->
    <template v-if="zweiFaktorSchritt === 'code' && uebrig === null">
      <p class="mb-4">
        {{
          mitWiederherstellung
            ? "Gib einen deiner Wiederherstellungscodes ein. Jeder Code lässt sich nur einmal verwenden."
            : "Gib den sechsstelligen Code aus deiner Authenticator-App ein."
        }}
      </p>
      <v-form @submit.prevent="anmelden">
        <v-text-field
            v-if="!mitWiederherstellung"
            v-model="code"
            prepend-icon="mdi-shield-key"
            label="Code"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="7"
            autofocus
            data-cy="zweiFaktorCode"
        />
        <v-text-field
            v-else
            v-model="wiederherstellungscode"
            prepend-icon="mdi-lifebuoy"
            label="Wiederherstellungscode"
            placeholder="XXXXX-XXXXX"
            autocomplete="off"
            autofocus
            data-cy="zweiFaktorWiederherstellung"
        />
        <v-alert v-if="fehler" type="error" text dense data-cy="zweiFaktorFehler">{{ fehler }}</v-alert>
        <v-btn
            type="submit"
            block
            large
            color="primary"
            :loading="laeuft"
            :disabled="!eingabeVollstaendig"
            data-cy="zweiFaktorAnmelden"
        >Bestätigen</v-btn>
      </v-form>
      <v-btn
          block
          text
          color="primary"
          class="mt-4"
          data-cy="zweiFaktorWiederherstellungLink"
          @click="umschalten"
      >{{ mitWiederherstellung ? "Code aus der App verwenden" : "Wiederherstellungscode verwenden" }}</v-btn>
      <v-btn block text class="mt-1" data-cy="zweiFaktorZurueck" @click="abbrechen">Zurück zur Anmeldung</v-btn>
    </template>

    <!-- Nach einem Wiederherstellungscode: Hinweis, bevor es weitergeht -->
    <template v-else-if="zweiFaktorSchritt === 'code'">
      <v-alert :type="uebrig <= 3 ? 'warning' : 'info'" text data-cy="zweiFaktorUebrig">
        Du hast dich mit einem Wiederherstellungscode angemeldet. Es
        {{ uebrig === 1 ? "bleibt noch 1 Code" : "bleiben noch " + uebrig + " Codes" }}.
        Hast du keinen Zugriff mehr auf deine Authenticator-App, richte die
        Zwei-Faktor-Anmeldung in deinem Benutzerprofil neu ein.
      </v-alert>
      <v-btn block large color="primary" data-cy="zweiFaktorWeiter" @click="$emit('angemeldet')">
        Weiter zu onDispatch
      </v-btn>
    </template>

    <!-- ================================================ Einrichtung -->
    <template v-else-if="zweiFaktorSchritt === 'einrichten'">
      <v-alert type="info" text dense>
        Deine Organisation verlangt die Zwei-Faktor-Anmeldung. Richte sie jetzt
        ein, um fortzufahren.
      </v-alert>

      <template v-if="!codes">
        <p>
          1. Scanne den QR-Code mit einer Authenticator-App (z. B. Microsoft
          Authenticator, Google Authenticator oder 1Password).
        </p>
        <div class="text-center mb-2">
          <v-progress-circular v-if="!einrichtung && !fehler" indeterminate color="primary"/>
          <img
              v-if="einrichtung"
              :src="einrichtung.qrCode"
              width="200"
              height="200"
              alt="QR-Code für die Authenticator-App"
              data-cy="zweiFaktorQr"
          />
        </div>
        <p v-if="einrichtung" class="caption">
          Scannen nicht möglich? Gib diesen Schlüssel in der App ein:<br>
          <code data-cy="zweiFaktorGeheimnis">{{ geheimnisLesbar }}</code>
        </p>
        <p>2. Gib den sechsstelligen Code ein, den die App anzeigt.</p>
        <v-form @submit.prevent="bestaetigen">
          <v-text-field
              v-model="code"
              prepend-icon="mdi-shield-key"
              label="Code"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="7"
              data-cy="zweiFaktorEinrichtenCode"
          />
          <v-alert v-if="fehler" type="error" text dense data-cy="zweiFaktorFehler">{{ fehler }}</v-alert>
          <v-btn
              type="submit"
              block
              large
              color="primary"
              :loading="laeuft"
              :disabled="!einrichtung || !codeVollstaendig"
              data-cy="zweiFaktorEinrichtenBestaetigen"
          >Bestätigen</v-btn>
        </v-form>
      </template>

      <template v-else>
        <v-alert type="success" text dense>Die Zwei-Faktor-Anmeldung ist eingerichtet.</v-alert>
        <p>
          <strong>Deine Wiederherstellungscodes</strong> – sie werden nur dieses
          eine Mal angezeigt. Mit ihnen kommst du in dein Konto, falls du keinen
          Zugriff auf deine Authenticator-App hast. Jeder Code gilt einmal.
        </p>
        <ul class="codes mb-3" data-cy="zweiFaktorCodes">
          <li v-for="c in codes" :key="c"><code>{{ c }}</code></li>
        </ul>
        <v-btn outlined block color="primary" class="mb-2" data-cy="zweiFaktorCodesHerunterladen" @click="herunterladen">
          <v-icon left>mdi-download</v-icon> Codes herunterladen
        </v-btn>
        <v-checkbox
            v-model="gesichert"
            label="Ich habe die Codes sicher aufbewahrt."
            data-cy="zweiFaktorCodesGesichert"
        />
        <v-btn block large color="primary" :disabled="!gesichert" data-cy="zweiFaktorWeiter" @click="$emit('angemeldet')">
          Weiter zu onDispatch
        </v-btn>
      </template>
      <v-btn block text class="mt-2" data-cy="zweiFaktorZurueck" @click="abbrechen">Abbrechen</v-btn>
    </template>
  </v-card-text>
</template>

<script>
import {mapGetters} from "vuex";
import {codesHerunterladen} from "../../util/wiederherstellungscodes";

/** Wie die Oberflaeche einen abgelaufenen zweiten Schritt behandelt. */
export const ABGELAUFEN = "ZWISCHENTOKEN_UNGUELTIG";

export default {
  name: "ZweiFaktorSchritt",
  data: () => ({
    code: "",
    wiederherstellungscode: "",
    mitWiederherstellung: false,
    laeuft: false,
    fehler: null,
    //Code-Schritt: verbleibende Wiederherstellungscodes nach deren Verwendung
    uebrig: null,
    //Einrichtung
    einrichtung: null,
    codes: null,
    gesichert: false,
  }),
  computed: {
    ...mapGetters(["zweiFaktorSchritt"]),
    codeVollstaendig() {
      return /^\d{6}$/.test(String(this.code || "").replace(/\s/g, ""));
    },
    eingabeVollstaendig() {
      return this.mitWiederherstellung
        ? String(this.wiederherstellungscode || "").replace(/[\s-]/g, "").length === 10
        : this.codeVollstaendig;
    },
    /** Schluessel in Vierergruppen - leichter abzutippen. */
    geheimnisLesbar() {
      return ((this.einrichtung && this.einrichtung.geheimnis) || "").replace(/(.{4})/g, "$1 ").trim();
    },
  },
  mounted() {
    if (this.zweiFaktorSchritt === "einrichten") this.einrichten();
  },
  methods: {
    umschalten() {
      this.mitWiederherstellung = !this.mitWiederherstellung;
      this.fehler = null;
    },
    abbrechen() {
      this.$store.dispatch("zweiFaktorAbbrechen");
    },
    async anmelden() {
      if (!this.eingabeVollstaendig || this.laeuft) return;
      this.laeuft = true;
      this.fehler = null;

      const ergebnis = await this.$store.dispatch(
        "zweiFaktorAnmelden",
        this.mitWiederherstellung
          ? {wiederherstellungscode: this.wiederherstellungscode}
          : {code: this.code}
      );
      this.laeuft = false;

      if (ergebnis && ergebnis.ok) {
        if (ergebnis.uebrig !== null && ergebnis.uebrig !== undefined) {
          this.uebrig = ergebnis.uebrig;
          return;
        }
        this.$emit("angemeldet");
        return;
      }

      if (ergebnis && ergebnis.code === ABGELAUFEN) {
        //fuenf Minuten um - zurueck zum Passwort, mit Hinweis
        this.$store.commit("CREATE_SNACKBAR", ergebnis.meldung);
        this.abbrechen();
        return;
      }

      this.code = "";
      this.fehler = (ergebnis && ergebnis.meldung) || "Das hat nicht geklappt.";
    },
    async einrichten() {
      this.fehler = null;
      const daten = await this.$store.dispatch("zweiFaktorEinrichten");
      if (daten && !daten.fehler) {
        this.einrichtung = daten;
      } else {
        this.fehler = (daten && daten.fehler && daten.fehler.meldung) || "Die Einrichtung konnte nicht gestartet werden.";
      }
    },
    async bestaetigen() {
      if (!this.codeVollstaendig || this.laeuft) return;
      this.laeuft = true;
      this.fehler = null;

      const ergebnis = await this.$store.dispatch("zweiFaktorBestaetigen", this.code);
      this.laeuft = false;

      if (ergebnis && ergebnis.ok) {
        this.codes = ergebnis.codes;
        return;
      }
      this.code = "";
      this.fehler = (ergebnis && ergebnis.meldung) || "Das hat nicht geklappt.";
      //abgelaufene Einrichtung: neu starten, damit ein frischer QR-Code erscheint
      if (ergebnis && ergebnis.code === "EINRICHTUNG_ABGELAUFEN") this.einrichten();
    },
    herunterladen() {
      codesHerunterladen(this.codes || [], this.einrichtung && this.einrichtung.konto);
    },
  },
};
</script>

<style scoped>
.codes {
  columns: 2;
  list-style: none;
  padding-left: 0;
}
</style>
